import { mat4, quat, ReadonlyVec2, vec2, vec3 } from 'gl-matrix';
import { RegistrationUndoService } from '~/domain/undo/RegistrationUndoService';
import { Environment } from '~/engine/data/Environment';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { ToolAction } from '../../ToolAction';

/** Rotation step size for keyboard micro-adjustments in radians (~0.5 degrees) */
const ROTATION_STEP_RAD = 0.5 * (Math.PI / 180);

export class RotationAction extends ToolAction {
    private readonly mouseButton: number;

    private _startMatrix?: mat4;
    private _startMouse?: vec2;
    private _imageCenterPatient?: vec3;
    private _registrationId?: string;
    private _didRotate = false;
    private readonly _undoService: RegistrationUndoService;

    public get isActive(): boolean {
        return this._startMatrix !== undefined;
    }

    private readonly angleScale = 0.005;

    constructor(mouseButton: number, undoService: RegistrationUndoService) {
        super();
        this.mouseButton = mouseButton;
        this._undoService = undoService;
    }

    public onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        _view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (
            button !== this.mouseButton ||
            !env?.secondaryImage ||
            !env?.registration ||
            env.registration.isApproved
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        this._startMouse = vec2.clone(coordinates);
        this._startMatrix = mat4.clone(env.registration.transformation);
        this._registrationId = env.registration.sopInstanceUid;
        this._didRotate = false;
        this._imageCenterPatient = vec3.clone(env.secondaryImage.grid.center);

        if (!this._imageCenterPatient) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (
            !this._startMatrix ||
            !this._startMouse ||
            !this._imageCenterPatient ||
            !env?.registration ||
            !(view instanceof PlaneView)
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const delta = vec2.sub(vec2.create(), coordinates, this._startMouse);
        const angle = -delta[0] * this.angleScale;

        const q = quat.create();
        quat.setAxisAngle(q, view.viewDirection, angle);
        const rotationMatrix = mat4.fromQuat(mat4.create(), q);

        const newMatrix = mat4.clone(this._startMatrix);
        mat4.translate(newMatrix, newMatrix, this._imageCenterPatient);
        mat4.multiply(newMatrix, newMatrix, rotationMatrix);
        mat4.translate(
            newMatrix,
            newMatrix,
            vec3.negate(vec3.create(), this._imageCenterPatient)
        );

        env.setRegistrationMatrix(newMatrix);
        this._didRotate = true;

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public onMouseRelease(
        _button?: number,
        _coordinates?: ReadonlyVec2,
        _view?: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (this._startMatrix && this._registrationId && this._didRotate) {
            this._undoService.pushState(
                this._registrationId,
                this._startMatrix
            );
            env?.registration?.syncTransformationToDataset();
        }
        this._startMatrix = undefined;
        this._startMouse = undefined;
        this._imageCenterPatient = undefined;
        this._registrationId = undefined;
        this._didRotate = false;
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Handles Ctrl/Cmd + Arrow key presses for micro-rotation
     * adjustments.
     */
    public onKeyPress(
        key: string,
        modifierKeys: ModifierKeys,
        view: TextureView,
        env?: Environment
    ): EventHandlerResult {
        if (
            !modifierKeys.hasCtrl ||
            !(view instanceof PlaneView) ||
            !env?.secondaryImage ||
            !env?.registration ||
            env.registration.isApproved
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        let angle = 0;
        if (key === 'ArrowLeft') {
            angle = ROTATION_STEP_RAD;
        } else if (key === 'ArrowRight') {
            angle = -ROTATION_STEP_RAD;
        }

        if (angle === 0) {
            // Ctrl/Cmd+Up and Down are the nudge chord, which turns nothing
            // here; consumed, so the chord never scrolls the slice instead.
            return new EventHandlerResult(
                key === 'ArrowUp' || key === 'ArrowDown'
                    ? EventHandlerResultType.EVENT_HANDLED
                    : EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const imageCenter = vec3.clone(env.secondaryImage.grid.center);
        const previousMatrix = mat4.clone(env.registration.transformation);

        const q = quat.create();
        quat.setAxisAngle(q, view.viewDirection, angle);
        const rotationMatrix = mat4.fromQuat(mat4.create(), q);

        const newMatrix = mat4.clone(previousMatrix);
        mat4.translate(newMatrix, newMatrix, imageCenter);
        mat4.multiply(newMatrix, newMatrix, rotationMatrix);
        mat4.translate(
            newMatrix,
            newMatrix,
            vec3.negate(vec3.create(), imageCenter)
        );

        env.setRegistrationMatrix(newMatrix);

        this._undoService.pushState(
            env.registration.sopInstanceUid,
            previousMatrix
        );
        env.registration.syncTransformationToDataset();

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public cancelAction(env?: Environment): void {
        if (this._startMatrix && env?.registration) {
            env.setRegistrationMatrix(mat4.clone(this._startMatrix));
        }
        this._startMatrix = undefined;
        this._startMouse = undefined;
        this._imageCenterPatient = undefined;
        this._registrationId = undefined;
        this._didRotate = false;
    }
}
